import React, { useRef } from 'react';
import { Row, Col, DatePicker, Button, Select, Table, Pagination } from 'antd';
import moment from 'moment';
import { Bar } from 'react-chartjs-2';
import { DownloadOutlined, BarChartOutlined, LineChartOutlined } from '@ant-design/icons';

import SpanComponent from '@sharedComponent/span-component';
import Header from '@sharedComponent/header/header';
import '@sharedComponent/round-bar';
import SkeletonComponent from '@sharedComponent/skeleton-component';

import '../affiliate-usage.scss';
import Counter from '@sharedComponent/counter';
import { datePickerValues, dateFormats, chartTypes } from '@constants';
import AppLoader from '@sharedComponent/loaders/app-loader';
import { disableFutureDate } from '@helpers';
import { AffiliateUsageExcelDownload } from '@sharedComponent/excel/excel';

const { RangePicker } = DatePicker;
const { Option } = Select;
const dateFormat = 'YYYY/MM/DD';

const AffiliateUsageCard = ({ title, value, desc, loading = false, rightCard = false }) => (
	<Col
		xl={11}
		lg={10}
		md={24}
		sm={24}
		xs={24}
		className={`status_card ${rightCard ? 'right_card' : ''} ${loading ? 'remove_style' : ''}`}
		style={{ marginTop: '10px' }}>
		<Col className="card_header">
			<Col className="card_header_name">{loading ? <SkeletonComponent width={'80%'} /> : title}</Col>
		</Col>
		<Col className="card_body">
			<Col className="checking">{loading ? <SkeletonComponent width={'40%'} height={20} /> : <Counter end={value || 0} />}</Col>
			<Col className="checking_number">{loading ? <SkeletonComponent width={'100%'} /> : desc}</Col>
		</Col>
	</Col>
);

const AffiliatePresentational = ({
	state,
	handleChange,
	dateFilter,
	dataRangeChanged,
	handleCampainList,
	affiliateUsageTotalVisitLoading,
	affiliateUsageTotalVisit,
	affiliateUsageAverageTotalVisitLoading,
	affiliateUsageAverageTotalVisit,
	affiliateUsageData,
	affiliateUsageChartdataLoading,
	affiliateUsageTabledataLoading,
	affiliateUsageTabledata,
	affiliateUsageDropdown,
	columns,
	option,
	calculateWidth,
	userDetails,
}) => {
	console.clear();
	var data = {
		option: option,
		labels: affiliateUsageData.labels || [],
		datasets: affiliateUsageData.datasetsTemp || [],
	};
	const ref = useRef(null);
	const divWidth = ref.current && ref.current.clientWidth;

	return (
		<Row className="affiliate_usage_container">
			<Col xl={24}>
				<Header title="Dashboard / Affiliate Usage" />
				<Col className="performance_report_container">
					<Row justify="space-between">
						<AffiliateUsageCard
							loading={affiliateUsageTotalVisitLoading}
							title="This Week"
							value={affiliateUsageTotalVisit}
							desc="Total Visits"
						/>
						<AffiliateUsageCard
							loading={affiliateUsageAverageTotalVisitLoading}
							title="This Week"
							value={affiliateUsageAverageTotalVisit}
							desc={'Average Length of Site visit'}
							rightCard
						/>
					</Row>

					<Row className="chart_area" style={{ paddingTop: 20, paddingBottom: 20 }}>
						<Col xl={24} style={{ width: '100%' }}>
							<Col className="chart_container">
								<Row className="chart_header">
									<Col xl={8}>
										<SpanComponent className="title">Affiliate Usage</SpanComponent>
										<SpanComponent style={{ marginLeft: 10 }}>
											<Select
												placeholder={'Affiliate'}
												mode="multiple"
												value={affiliateUsageData.campaignValue}
												maxTagCount={1}
												allowClear={true}
												onChange={handleCampainList}
												style={{ minWidth: 120 }}>
												<Option value="ALL">All</Option>
												{affiliateUsageDropdown.map((value, i) => (
													<Option value={value} key={i}>
														{value}
													</Option>
												))}
											</Select>
										</SpanComponent>
									</Col>
									<Col xl={16} className="chart_header_right_area">
										<Row>
											<Col className="actions chatType">
												<Button
													onClick={() => {
														handleChange('chartType', chartTypes.BAR);
													}}
													style={{
														height: '32px',
														padding: '0px',
													}}
													type={state.chartType === chartTypes.BAR ? 'primary' : null}
													icon={<BarChartOutlined />}
													size={'middle'}
												/>
											</Col>
											<Col className="actions chatType">
												<Button
													onClick={() => {
														handleChange('chartType', chartTypes.LINE);
													}}
													style={{
														height: '32px',
														padding: '0px',
													}}
													type={state.chartType === chartTypes.LINE ? 'primary' : null}
													icon={<LineChartOutlined />}
													size={'middle'}
												/>
											</Col>
											<Col className="actions">
												<RangePicker
													disabledDate={disableFutureDate}
													disabled={affiliateUsageData.dateFilter !== datePickerValues.CUSTOM}
													value={[
														moment(affiliateUsageData.startDate, dateFormats.MM_DD_YYYY),
														moment(affiliateUsageData.endDate, dateFormats.MM_DD_YYYY),
													]}
													format={dateFormats.MM_DD_YYYY}
													onCalendarChange={(e, value) => {
														dataRangeChanged(value);
													}}
												/>
											</Col>
											<Col className="actions">
												<Select
													defaultValue={datePickerValues.LAST}
													style={{ width: 120 }}
													onSelect={(value) => {
														dateFilter(value);
													}}>
													<Option value={datePickerValues.LAST}>Last 7 days</Option>
													<Option value={datePickerValues.YEST}>Yesterday</Option>
													<Option value={datePickerValues.THIS_WEEK}>This week</Option>
													<Option value={datePickerValues.THIS_MONTH}>This month</Option>
													<Option value={datePickerValues.CUSTOM}>Custom</Option>
												</Select>
											</Col>
										</Row>
									</Col>
								</Row>
								<Col xl={24} className="scroll-horizental-histo" ref={ref}>
									{affiliateUsageChartdataLoading ? (
										<Col style={{ minHeight: 400, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
											<AppLoader width="5%" height="10%" />
										</Col>
									) : (
										<Bar data={data} type={state.chartType} width={calculateWidth(divWidth)} height={500} options={option} />
									)}
								</Col>
							</Col>
						</Col>
					</Row>
					<Row className="chart_area">
						<Col xl={24} style={{ width: '100%' }}>
							<Col className="chart_container">
								<Row className="chart_header">
									<Col xl={8}>
										<SpanComponent className="title">Affiliate Usage</SpanComponent>
									</Col>
									<Col xl={16} className="chart_header_right_area">
										<Row>
											<Col className="export_area">
												<Button type="primary" className="excel_download" icon={<DownloadOutlined />} size={'middle'}>
													Export to EXCEL
													<AffiliateUsageExcelDownload
														style={{ visibility: 'hidden' }}
														dataSet={affiliateUsageTabledata}
														userDetails={userDetails}
														filename={`${moment(new Date()).format(dateFormat)}_Affiliate_Usage_Report`}
													/>
												</Button>
											</Col>
										</Row>
									</Col>
									<Col xl={24} className="aff_usage_table_container">
										{affiliateUsageTabledataLoading ? (
											<Col style={{ minHeight: 400, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
												<AppLoader width="5%" height="10%" />
											</Col>
										) : (
											<Table
												pagination={{
													position: ['bottomCenter', 'bottomCenter'],
													defaultPageSize: 5,
													showSizeChanger: true,
													pageSizeOptions: ['5', '10'],
												}}
												locale={{ emptyText: 'No Usage Report data to display' }}
												dataSource={affiliateUsageTabledata}
												columns={columns}
											/>
										)}
									</Col>
								</Row>
							</Col>
						</Col>
					</Row>
				</Col>
			</Col>
		</Row>
	);
};

export default AffiliatePresentational;
